/**
 * Real customer reviews, transcribed verbatim from the spa's Google Business
 * Profile on 2026-09-11.
 *
 * This file replaced five invented testimonials on /testimonials and three more
 * on the home page. Those were written copy attached to stock portraits from
 * randomuser.me, which is exactly what FTC 16 CFR §465.2 prohibits a business
 * from publishing about itself, and the page was already being served into
 * Google's generative AI results.
 *
 * Rules for editing this file:
 *
 *  - `text` is verbatim. Do not fix spelling, grammar or punctuation, and do
 *    not shorten. If a quote is too long for a layout, change the layout.
 *  - Only add reviews that are actually published on the Google profile linked
 *    below, so any claim here can be checked against a public source.
 *  - `treatment` describes what the reviewer themselves named. It is not a
 *    label we assign them.
 *  - No photographs. We have no licence to a reviewer's likeness, and the
 *    previous stock faces are what made the old testimonials fraudulent rather
 *    than merely generic. Initials only.
 *  - No Review or AggregateRating structured data. Marking up our own site's
 *    testimonials as a rating is self-serving review markup, against Google's
 *    policy and not independently checkable. The real rating lives on the
 *    Google profile, which is what the AI assistants read anyway.
 *
 * How to re-transcribe from Google Maps, including the parts that do not work
 * the obvious way: documents/history/2026-09-seo-round-2.md, section 1.
 */

export interface CustomerReview {
  /** Display name: first name plus last initial, as shown publicly on Google. */
  name: string;
  initials: string;
  /** 1-5, as posted. */
  rating: number;
  /** The treatment the reviewer named, for the caption line. */
  treatment: string;
  /** Verbatim review text. Never edited. */
  text: string;
}

export const GOOGLE_PROFILE_URL =
  'https://www.google.com/maps/place/Healing+Lotus+Skin+Spa/@32.9875495,-96.8376683,17z/data=!4m8!3m7!1s0x864c233d285ae497:0x1e61aadb68c55d3d!8m2!3d32.9875495!4d-96.8376683!9m1!1b1!16s%2Fg%2F11w8k94x92';

/**
 * Profile-wide figures as read on the capture date. Stated with the date in the
 * UI rather than as a bare number, because they move and we do not re-read them
 * on every deploy.
 */
export const GOOGLE_RATING_SNAPSHOT = {
  average: 4.9,
  count: 137,
  asOf: 'September 2026',
} as const;

/**
 * Ordered so the treatments a visitor might search for come first. Every one of
 * these names a specific service, which is also what Google matches on when it
 * picks the review snippet to show under a local result.
 */
export const customerReviews: CustomerReview[] = [
  {
    name: 'Jacqueline B.',
    initials: 'JB',
    rating: 5,
    treatment: 'La Mer facial and BoJing meridian sculpting',
    text: 'I came in for a LA MER The Revitalizing Hydration Facial and Facial Meridian Sculpting (BoJing). Tory was fantastic! She took her time with me and was very gentle and happy to explain anything. The facility was so beautiful and clean! I am already planning to go back soon and take my mom!',
  },
  {
    name: 'Julia L.',
    initials: 'JL',
    rating: 5,
    treatment: 'Head spa',
    text: 'Very impressed by Healing Lotus Spa that recently opened in the Dallas area! Each room had amazing attention to detail and gave very relaxing energy. The inside was gorgeous and the head spa service is something I’ve never experienced before, definitely a unique luxurious treatment.',
  },
  {
    name: 'Zoe Q.',
    initials: 'ZQ',
    rating: 5,
    treatment: 'Facial with sound therapy',
    text: 'Got a facial today and tbh it was really calming. They did a lot of massage on my shoulders and neck, not just face, which I liked. The sound therapy in the background, kind of soft waves and chimes, put me half to sleep. Skin felt fresh after, not sticky, just relaxed.',
  },
  {
    name: 'Liana S.',
    initials: 'LS',
    rating: 5,
    treatment: 'Head therapy and facial gua sha',
    text: 'Very nice and therapeutic experience! Tory was extremely gentle and took really good care of me, I walked out feeling very refreshed and glowing. Loved the head therapy and the facial guasha, I literally fell into deep sleep during the head massages. Would highly recommend and definitely come back!',
  },
  {
    name: 'Annie W.',
    initials: 'AW',
    rating: 5,
    treatment: 'Hydrating facial',
    text: 'Artistic, calm space that’s so relaxing. I did the hydrating facial and my skin felt plump and quiet after, no redness, just soft. Tory was so professional and really knew what she was doing. Music low, lighting gentle, the whole thing felt like a slow reset after a long week.',
  },
  {
    name: 'Ruoyi G.',
    initials: 'RG',
    rating: 5,
    treatment: 'La Mer facial',
    text: 'The La Mer facial is perfection. Their guasha is very unique, super satisfying, alleviated my eye strain headache areas. Skin was glowing after! Incredible service (thank you!!), beautiful facility, will def be back',
  },
  {
    name: 'Alice B.',
    initials: 'AB',
    rating: 5,
    treatment: 'La Mer facial',
    text: 'We had a wonderful experience here! Thank you to Tory and Eden for taking great care of us. We had the La Mer treatment and my face was glowing so much. This felt like more than a facial, it was an experience that included the body and soul!! Will be coming back again!',
  },
  {
    name: 'Xiao L.',
    initials: 'XL',
    rating: 5,
    treatment: 'Facial and scalp therapy',
    text: 'If you need a facial place which relax you from day to day chaos, this is the place to come. PS: Tory did a good job in my facial and scalp therapy.',
  },
];

/** The three shown on the home page, so both pages stay in sync. */
export const featuredReviews = customerReviews.slice(0, 3);
